import axiosBaseConfig from "./axios";

export type IdfaOrderType = "keywords" | "direct";

/** Only installs come with IDFAs from the contractor. */
export const hasIdfaReport = (orderType?: string | null) =>
  orderType === "keywords" || orderType === "direct";

export type IdfaImportStatus =
  | "imported"
  | "duplicate"
  | "unmatched"
  | "ambiguous"
  | "skipped"
  | "failed";

export type IdfaImportedFile = {
  sourcePath: string;
  fileName: string;
  country: string | null;
  rows: number;
  skippedRows: number;
  invalidIdfas: number;
  status: IdfaImportStatus;
  reason: string | null;
  order: {
    orderType: IdfaOrderType;
    orderId: number;
    hashId: number | null;
    appTitle: string | null;
    country: string | null;
  } | null;
};

export type IdfaImportResult = {
  files: IdfaImportedFile[];
  importedFiles: number;
  importedRows: number;
};

export type IdfaExportFormat = "csv" | "xlsx";

// Nest answers errors as { message }; a download asked for a Blob gets it as one.
const errorMessage = async (error: any, fallback: string) => {
  let data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch {
      data = null;
    }
  }
  const message = Array.isArray(data?.message)
    ? data.message.join(", ")
    : data?.message;
  return new Error(message || fallback);
};

/**
 * Sends the contractor's archive (or one .xlsx). With a target every file
 * goes to that order; without one each goes to the order its archive names.
 */
export const importIdfaReports = async (
  file: File,
  target?: { orderType: string; orderId: number },
): Promise<IdfaImportResult> => {
  const form = new FormData();
  // The archive name carries the order numbers; sent as a field so it
  // arrives in UTF-8 whatever the browser does with the part's file name.
  form.append("fileName", file.name);
  if (target) {
    form.append("orderType", target.orderType);
    form.append("orderId", String(target.orderId));
  }
  form.append("file", file);
  try {
    const { data } = await axiosBaseConfig.post(
      "admin/idfa-reports/import",
      form,
    );
    return data;
  } catch (error) {
    throw await errorMessage(error, "The archive was not imported");
  }
};

/** Saves an order's IDFAs as CSV or XLSX, times in the client's timezone. */
export const downloadIdfaReport = async (
  orderType: string,
  orderId: number,
  format: IdfaExportFormat,
) => {
  try {
    const response = await axiosBaseConfig.get("admin/idfa-reports/export", {
      params: { orderType, orderId, format },
      responseType: "blob",
    });
    const disposition = String(response.headers["content-disposition"] || "");
    const fileName =
      /filename="([^"]+)"/.exec(disposition)?.[1] ||
      `idfa-${orderId}.${format}`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    throw await errorMessage(error, "The report was not downloaded");
  }
};
